import { useRef } from 'react';
import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { DownloadIcon } from 'lucide-react';

export interface DownloadIconButtonProps {
  /** Names the control and labels its tooltip. */
  label: string;
  /** Awaited: clicks are ignored until it settles. */
  onDownload: () => Promise<void>;
  isDownloading: boolean;
  /** Match the neighbouring tooltips, e.g. `bottom` in a SidePanelHeader action row. */
  tooltipSide?: 'top' | 'bottom';
}

export const DownloadIconButton = ({
  label,
  onDownload,
  isDownloading,
  tooltipSide = 'top',
}: DownloadIconButtonProps) => {
  // `isDownloading` disables the button a render late, so a double click would start two downloads.
  const inFlight = useRef(false);

  const handleClick = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await onDownload();
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        {/* The span is the trigger because a disabled button swallows pointer events. It wraps the
            button in every state: swapping the trigger element would remount the button and drop focus. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={isDownloading}
              aria-label={label}
              onClick={() => void handleClick()}
            >
              {isDownloading ? <Spinner size="sm" /> : <DownloadIcon />}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide}>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
